import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Group, Stack, Text } from '@mantine/core';

import {
  useGetOrganisation,
  usePatchOrganisationBoard,
} from 'api/organisations';
import { OrganisationViewCardSection } from 'components/Organisations';
import { Form, TextField } from 'components/shared';
import { useShowNotification } from 'hooks';
import { LogoutIcon, TrashIcon } from 'icons';
import type { BoardType } from 'types';

import { BoardLeaveModal } from './BoardLeaveModal';
import { NameEditModal } from './NameEditModal';

type Props = {
  board: BoardType;
};

type FormData = {
  name: string;
};

export const GeneralSettings = ({ board }: Props) => {
  const { organisationId, boardId } = useParams();
  if (!organisationId || !boardId) throw new Error('Missing URL params');

  const [nameEditModalOpened, setNameEditModalOpened] = useState(false);
  const { data: organisation } = useGetOrganisation({
    pathParams: { organisationId },
  });
  const { isAdministrator } = organisation || {};

  const { t } = useTranslation('organisations');

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  return (
    <Stack spacing="xl">
      <OrganisationViewCardSection
        title={t('organisationView.boards.generalSettings.profile.title')}
        headerRight={<Text>ID: {board.id}</Text>}
      >
        <Text
          sx={theme => ({
            fontSize: 15,
            fontWeight: 'bold',
            marginBottom: theme.other.spacing(1),
          })}
        >
          {t('organisationView.boards.generalSettings.profile.name.title')}
        </Text>
        <Text
          sx={theme => ({
            fontSize: 13,
            color: theme.colors.gray[6],
            marginBottom: theme.other.spacing(2),
          })}
        >
          {isAdministrator
            ? t(
                'organisationView.boards.generalSettings.profile.name.description.default',
              )
            : t(
                'organisationView.boards.generalSettings.profile.name.description.adminOnly',
              )}
        </Text>
        <Group spacing="lg">
          <Text>{board.name}</Text>
          {isAdministrator && (
            <Button
              variant="default"
              onClick={() => setNameEditModalOpened(true)}
            >
              {t(
                'organisationView.boards.generalSettings.profile.name.editButton',
              )}
            </Button>
          )}
        </Group>
        <NameEditModal
          opened={nameEditModalOpened}
          onClose={() => setNameEditModalOpened(false)}
          board={board}
        />
      </OrganisationViewCardSection>
      {!isAdministrator && (
        <OrganisationViewCardSection
          title={t('organisationView.boards.generalSettings.leave.title')}
          description={t(
            'organisationView.boards.generalSettings.leave.description',
          )}
          headerRight={
            <>
              <Button
                leftIcon={<LogoutIcon />}
                color="red"
                onClick={() => setDeleteModalOpened(true)}
              >
                {t('organisationView.boards.generalSettings.leave.button')}
              </Button>
              <BoardLeaveModal
                opened={deleteModalOpened}
                onClose={() => setDeleteModalOpened(false)}
              />
            </>
          }
        />
      )}
    </Stack>
  );
};
